import * as React from "react";
import { useSearchParams } from "react-router";

import {
  createEmptyLayout,
  isTemporaryLayout,
  LayoutRecord,
} from "@/shared/domain/layouts";
import { SearchParams } from "@/shared/environment";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import { useWorkspaceLoaderData } from "../loader";

interface SetActiveLayoutOptions {
  skipReload?: boolean;
}

/**
 * Visualizer layout app state
 */
export interface LayoutState {
  activeLayout: LayoutRecord;
  setActiveLayout: (
    layout: LayoutRecord,
    options?: SetActiveLayoutOptions,
  ) => void;
  isLoading: boolean;
  error: Error | null;
  skipLayoutRehydration: boolean;
}

/**
 * What `settings.layoutId` should record for `layout`, or `undefined` to leave
 * the recorded value untouched.
 *
 * The reference is a display label; the bound panels always win at open. A
 * saved layout records its `layout_id`, and a temporary (unsaved) layout has no
 * saved-layout identity and records an explicit null. The exception is the
 * layout a workspace opens with, which `isInitialLayout` marks: a restored
 * workspace opens with the temporary layout while its config may carry the
 * sharer's `layoutId`, and that label must survive a reshare rather than be
 * nulled by mount.
 */
export function layoutIdSettingForChange(
  layout: LayoutRecord,
  isInitialLayout: boolean,
): { layoutId: string | null } | undefined {
  const isTemporary = isTemporaryLayout(layout);
  if (isInitialLayout && isTemporary) {
    return undefined;
  }
  return { layoutId: isTemporary ? null : layout.layout_id };
}

export function useLayoutState(): LayoutState {
  const [_, setSearchParams] = useSearchParams();
  const workspaceResult = useWorkspaceLoaderData();
  const dispatch = useVizDispatch();

  // Active layout state
  const [activeLayout, setActiveLayoutState] = React.useState<LayoutRecord>(
    () => {
      // If we have a layout from the loader data, use that
      if (workspaceResult.configSource?.type === "layout") {
        return workspaceResult.configSource.value;
      }

      // Otherwise, use the initial layout
      return createEmptyLayout();
    },
  );

  // Flag to skip the next layout reload. Used when creating a new layout
  const [skipLayoutRehydration, setSkipLayoutRehydration] =
    React.useState(false);

  const setActiveLayout = React.useCallback(
    (layout: LayoutRecord, options?: SetActiveLayoutOptions) => {
      setSkipLayoutRehydration(options?.skipReload ?? false);
      setActiveLayoutState(layout);
    },
    [],
  );

  // The layout the workspace opened with. The mount-time skip keys off which
  // layout is being synced rather than off how many times the effect has run:
  // React 18 StrictMode runs the effect twice on mount, and a run counter would
  // read the second run as a switch away from the initial layout and null out a
  // restored workspace's recorded `layoutId`.
  const initialLayout = React.useRef(activeLayout);
  React.useEffect(
    function syncActiveLayoutToSettings() {
      const update = layoutIdSettingForChange(
        activeLayout,
        activeLayout === initialLayout.current,
      );
      if (update !== undefined) {
        dispatch(actions.setWorkspaceSettings(update));
      }
    },
    [activeLayout, dispatch],
  );

  React.useEffect(
    function syncActiveLayoutToUrl() {
      // The URL carries the active layout by name; a `?layoutId=` deep link
      // is consumed by the loader and dropped here so both never coexist.
      if (isTemporaryLayout(activeLayout)) {
        // Remove layout name from URL if it's the temporary layout
        setSearchParams(
          (oldParams) => {
            const params = new URLSearchParams(oldParams);
            params.delete(SearchParams.LAYOUT);
            params.delete(SearchParams.LAYOUT_ID);
            return params;
          },
          { replace: true },
        );
      } else {
        // Otherwise, sync URL with the new layout name
        setSearchParams(
          (oldParams) => {
            const params = new URLSearchParams(oldParams);
            params.set(SearchParams.LAYOUT, activeLayout.name);
            params.delete(SearchParams.LAYOUT_ID);
            return params;
          },
          { replace: true },
        );
      }
    },
    [activeLayout, setSearchParams],
  );

  return {
    activeLayout: activeLayout,
    error: null,
    isLoading: false,
    setActiveLayout,
    skipLayoutRehydration,
  };
}
